import { useState } from "react";

import { useRouteMatch } from "react-router-dom";

import { postTrackingData } from "@/api/endpoints/track";
import DownloadIcon from "@/assets/icons/download.svg";
import ShareIcon from "@/assets/icons/share.svg";
import Container from "@/components/Container";
import Episodes from "@/containers/Episodes";
import MentionCardWithLoader, {
  SkeletonMentionCard,
} from "@/containers/MentionCard";
import MentionCardsProvider from "@/containers/MentionCardsProvider";
import ShareModal from "@/containers/ShareModal";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import { ROUTES } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { cx } from "@/utils";
import FilterButton from "@/views/MentionsView/FilterButton";
import DateRangeButton from "@/views/MentionsView/partials/DateRangeButton";
import MentionPagination from "@/views/MentionsView/partials/MentionPagination";
import Tag404 from "@/views/tags/[slug]/Tag404";
import { ALL_MENTION_VIEW_DATE_RANGES } from "@/views/tags/ranges";
import AllEarsTitle from "@components/AllEarsTitle";
import Button from "@components/Button";
import Typography from "@components/Typography";

import EmptyFavorites from "../../favorites/empty";
import TagExportModal from "./partials/TagExportModal";
import TagShareModal from "./partials/TagShareModal";

import styles from "./tag.module.css";

export const TagInnerView = () => {
  const { tag, loading, mentionCards } = useMentionCards();
  const isFavoritePage = useRouteMatch(ROUTES.FAVORITES_ORGID);
  const [isSharing, setIsSharing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // note: we early exit if there's no tag & we're not on favorite page
  if (!tag && !isFavoritePage) return <Tag404 />;
  if (isFavoritePage && mentionCards.length === 0) return <EmptyFavorites />;

  const title = isFavoritePage
    ? L.NAV_FAVORITE_ALERTS
    : tag?.name
      ? `${L.MENTIONS_TAGS_TAG_TITLE}: ${tag?.name}`
      : "";

  return (
    <>
      <AllEarsTitle title={title} />
      <Container fullWidth className={cx(styles.row, styles.top)}>
        <Typography
          margin="none"
          variant="h1"
          data-testid="mentions-title"
          wordBreak
          className={styles.title}
        >
          {title}
        </Typography>
        <span className={styles.pushApart} />

        <Button
          data-testid="open-export-modal"
          onClick={() => {
            postTrackingData(EVENT.EXPORT_TAG, tag);
            setIsExporting(true);
          }}
          variant="tertiary"
          icon={DownloadIcon}
        >
          {L.EXPORT}
        </Button>

        <Button
          data-testid="open-share-modal"
          onClick={() => {
            postTrackingData(EVENT.SHARE_TAG, tag);
            setIsSharing(true);
          }}
          variant="tertiary"
          icon={ShareIcon}
        >
          {L.SHARE}
        </Button>
      </Container>
      <Container fullWidth className={styles.row}>
        <DateRangeButton ranges={ALL_MENTION_VIEW_DATE_RANGES} />
        <FilterButton />
        <span className={styles.pushApart} />
      </Container>
      <Container fullWidth>
        <Episodes>
          {loading ? (
            <>
              <SkeletonMentionCard />
              <SkeletonMentionCard />
              <SkeletonMentionCard />
            </>
          ) : (
            mentionCards?.map((mentionCard) => (
              <MentionCardWithLoader
                {...mentionCard}
                key={mentionCard?.episode?.guid}
              />
            ))
          )}
        </Episodes>

        <MentionPagination
          v4
          disabled={loading}
          className={styles.pagination}
        />
      </Container>
      <ShareModal v4 />

      <TagShareModal show={isSharing} onHide={() => setIsSharing(false)} />

      <TagExportModal show={isExporting} onHide={() => setIsExporting(false)} />
    </>
  );
};

const TagView = () => (
  <MentionCardsProvider>
    <TagInnerView />
  </MentionCardsProvider>
);

export default TagView;
